import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {clsx} from 'clsx'
import {RelativeTime} from '@primer/react'
import {PullRequestState} from '../types'
import {useCallback, useEffect, useRef} from 'react'
import styles from './PullRequestMiniHeader.module.css'
import {StateIcon} from './StateIcon'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {getRouteConfig} from '../routes/route-manager'
import {useMergeStatusButtonData, type ValidatedMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {MergeStatusPanel} from './toolbar/MergeStatusPanel'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {OpenOverviewPanelButton} from './toolbar/OpenOverviewPanelButton'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import type {HeaderPageData} from '../page-data/payloads/header'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import type {PullRequestHeaderProps} from './PullRequestHeader'
import {useHydrateMarkerCounts} from '../hooks/use-hydrate-marker-counts'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isLoggedIn} from '@github-ui/client-env'
import {OpenAgentsPanelButton} from './toolbar/OpenAgentsPanelButton'
import {OpenAlertsPanelButton} from './toolbar/OpenAlertsPanelButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {RefreshButton} from './toolbar/RefreshButton'
import {useIsComparisonOutdated} from '../stores/layout-selectors'

function getStatusObj(state: PullRequestState) {
  switch (state) {
    case PullRequestState.Merged:
      return {text: 'merged', datetimeKey: 'mergedTime' as const}
    case PullRequestState.Closed:
      return {text: 'closed', datetimeKey: 'closedTime' as const}
    default:
      return {text: 'opened', datetimeKey: 'createdTime' as const}
  }
}

export type PullRequestMiniHeaderProps = HeaderPageData & {
  activeRoute: PullRequestActiveRoute
}

/**
 * LivePullRequestMiniHeader is a thin wrapper around PullRequestMiniHeader that calls `useHeaderPageData` to automatically
 * update the header when the alive channel is triggered with `{title_updated: true}`, `{git_updated: true}`, or when
 * there is an update to the PullRequestState channel. This lives here instead of the base page so that only the header
 * re-renders on cache updates, not the entire page.
 */
export function LivePullRequestMiniHeader({
  aliveChannels,
  bannersData,
  activeRoute,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
  viewSettings,
}: PullRequestHeaderProps) {
  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
  })
  return <PullRequestMiniHeader {...headerData} activeRoute={activeRoute} />
}

export function PullRequestMiniHeader({
  pullRequest,
  repository,
  mergeStatusButtonData,
  aliveChannels,
  activeRoute,
}: PullRequestMiniHeaderProps) {
  const {isSticky, sensorRef} = useStickyHeader()
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const hasValidatedMergeData = validatedMergeData !== null

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  const {text: statusText, datetimeKey} = getStatusObj(pullRequest.state)
  const datetime = pullRequest[datetimeKey]
  useHydrateMarkerCounts()

  return (
    <>
      <div className={clsx(stickyStyles.stickyHeader, styles.miniHeader)}>
        <StateIcon state={pullRequest.state} className="ml-1" />
        <span className={clsx(styles.number, 'f5 fgColor-muted')}>#{pullRequest.number}</span>
        {isSticky && (
          <span className={clsx(styles.title, 'f5 text-bold fgColor-default')}>
            <VerifiedHTMLText className="markdown-title" html={pullRequest.titleHtml} />
          </span>
        )}
        {!isSticky && datetime && (
          <span className="f5 fgColor-muted">
            {statusText} <RelativeTime datetime={datetime} />
          </span>
        )}
        <div className={styles.actions}>
          {validatedMergeData && (
            <MiniHeaderMergeOrRefresh
              validatedMergeData={validatedMergeData}
              isDockedMode={isDockedMode}
              onToggleDockedPanel={handleToggleDockedMergePanel}
              mergeStatusButtonRef={mergeStatusButtonRef}
              pullRequestAliveChannel={aliveChannels.pullRequest}
            />
          )}
          <PullRequestCodeButton
            codespacesEnabled={repository.codespacesEnabled}
            headBranch={pullRequest.headBranch}
            isEnterprise={repository.isEnterprise}
            pullRequestId={pullRequest.id}
            pullRequestNumber={pullRequest.number}
            repository={repository}
            size="small"
            iconOnly
          />
          <OpenOverviewPanelButton aliveChannel={aliveChannels.pullRequest} className="hide-sm hide-md" />
          {isFeatureEnabled('coding_agent_logs_in_pr') && (
            <OpenAgentsPanelButton
              repository={repository}
              pullRequestNumber={pullRequest.number}
              className="hide-sm hide-md"
            />
          )}
          <OpenCommentsPanelButton aliveChannels={aliveChannels} className="hide-sm" />
          <OpenAlertsPanelButton endCommitOid={pullRequest.headSha} className="hide-sm" />
          {isFeatureEnabled('copilot_chat_docked_panel') && <OpenCopilotChatPanelButton />}
        </div>
      </div>
      <div ref={sensorRef} className={styles.stickyHeaderSentinel} />
    </>
  )
}

function MiniHeaderMergeOrRefresh({
  validatedMergeData,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  pullRequestAliveChannel,
}: {
  validatedMergeData: ValidatedMergeStatusButtonData | null
  isDockedMode: boolean
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: React.RefObject<HTMLButtonElement | null>
  pullRequestAliveChannel: string
}) {
  const isComparisonOutdated = useIsComparisonOutdated()

  return (
    <>
      {isLoggedIn() && !isComparisonOutdated && validatedMergeData && (
        <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
          <MergeStatusButtonWrapper
            channels={validatedMergeData.channels}
            headSha={validatedMergeData.headSha}
            helpUrl={validatedMergeData.helpUrl}
            viewerLogin={validatedMergeData.viewerLogin}
            isDockedMode={isDockedMode}
            onToggleDockedPanel={onToggleDockedPanel}
            mergeStatusButtonRef={mergeStatusButtonRef}
            variant="invisible"
            buttonClasses="hide-sm"
          />
          {isDockedMode && (
            <MergeStatusPanel
              helpUrl={validatedMergeData.helpUrl}
              viewerLogin={validatedMergeData.viewerLogin}
              channels={validatedMergeData.channels}
            />
          )}
        </MergeMethodContextProvider>
      )}
      <RefreshButton aliveChannel={pullRequestAliveChannel} />
    </>
  )
}
